import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  setTokenExpireTime, 
  setShowRefreshWarning, 
  setRefreshTimeoutId,
  setWarningTimeoutId,
  clearTimeouts,
  setSessionExpired
} from '../slices/authSlice';
import { cache, cacheGet } from '../utils/cache';
import { RefreshTokenCreateService } from '../services/auth.service';
import axios from 'axios';
import configs from '../config/app';


const useTokenRefresh = () => {
  const dispatch = useDispatch();
  const { 
    tokenExpireTime, 
    showRefreshWarning
  } = useSelector((state) => state.auth);

  // Function to perform logout
  const performLogout = useCallback(() => {
    if(configs.env !== 'production') {
      console.log('Performing logout...');
    }
    // First, clear all timeouts to prevent further state changes
    dispatch(clearTimeouts());
    
    // Clear all storage items completely
    sessionStorage.clear();
    localStorage.clear();
    
    // Ensure session expired state is set
    dispatch(setSessionExpired(true));
    
    // Use setTimeout to allow React to finish current render cycle before redirect
    setTimeout(() => {
      // Use replace instead of href to prevent back button issues
      window.location.replace(configs.logoutRedirectUrl);
      
      // Fallback: If replace doesn't work for some reason, force a reload after a short delay
      setTimeout(() => {
        if (configs.logoutRedirectUrl && window.location.pathname !== configs.logoutRedirectUrl && window.location.pathname !== '/') {
          if(configs.env !== 'production') {
            console.log('Fallback: Force reloading page...');
          }
          window.location.href = configs.logoutRedirectUrl;
        } else {
          window.location.reload();
        }
      }, 1000);
    }, 100); // Increased timeout slightly to ensure state changes complete
  }, [dispatch]);

  // Function to setup token refresh timing
  const setupTokenRefresh = useCallback(() => {
    const apiToken = cacheGet('apiToken', true, null);
    
    if (!apiToken || !apiToken.jwtToken) {
      if(configs.env !== 'production') {
        console.log('No API token found, skipping token refresh setup');
      }
      return;
    }

    // Clear existing timeouts
    dispatch(clearTimeouts());

    // Calculate expiration time
    let expireTime;
    
    try {
      // Try to decode JWT to get expiration time
      const tokenPayload = JSON.parse(atob(apiToken.jwtToken.split('.')[1]));
      if (tokenPayload.exp) {
        expireTime = new Date(tokenPayload.exp * 1000);
        if(configs.env !== 'production') {
          console.log('Token expiration found in JWT:', expireTime.toISOString());
        }
      }
    } catch (error) {
      console.warn('Could not decode JWT token:', error);
    }

    // Fallback to 15 minutes from now if we can't get exp time
    if (!expireTime || expireTime <= new Date()) {
      expireTime = new Date(Date.now() + 15 * 60 * 1000);
      if(configs.env !== 'production') {
        console.log('Using fallback expiration time (15min):', expireTime.toISOString());
      }
    }

    dispatch(setTokenExpireTime(expireTime.toISOString()));

    const currentTime = new Date();
    const timeUntilExpire = expireTime - currentTime;


    if(configs.env !== 'production') {
      console.log({
        currentTime: currentTime.toISOString(),
        expireTime: expireTime.toISOString(),
        timeUntilExpireMs: timeUntilExpire,
        timeUntilExpireSec: Math.round(timeUntilExpire / 1000),
        timeUntilExpireMin: Math.round(timeUntilExpire / 60000)
      });
    }
    // Show warning at 5 minutes before expiration
    const warningTime = timeUntilExpire - (5 * 60 * 1000); // 5 minutes before expiration

    if (warningTime > 0) {
      // const warningTimeoutId = 
      setTimeout(() => {
        if(configs.env !== 'production') {
          console.log('Token refresh warning triggered - 5 minutes until expiration');
        } 
        dispatch(setShowRefreshWarning(true));
      }, warningTime);
      if(configs.env !== 'production') {
        console.log('Warning will show in:', Math.round(warningTime / 1000), 'seconds');
      }
      // dispatch(setWarningTimeoutId(warningTimeoutId));
    } else if (timeUntilExpire > 0) {
      // If less than 5 minutes remaining, show warning immediately
      if(configs.env !== 'production') {
        console.log('Less than 5 minutes remaining - showing warning immediately');
      }
      dispatch(setShowRefreshWarning(true));
    }

    // Set session expired at expiration time
    if (timeUntilExpire > 0) {
      const refreshTimeoutId = setTimeout(() => {
        if(configs.env !== 'production') {
          console.log('Token has expired - performing logout');
        }
        // Hide warning first to prevent UI flash
        dispatch(setShowRefreshWarning(false));
        // Set session expired first
        dispatch(setSessionExpired(true));
        // Perform logout when token expires
        performLogout();
      }, timeUntilExpire);
      if(configs.env !== 'production') {
        console.log('Token will expire and logout in:', Math.round(timeUntilExpire / 1000), 'seconds');
      }
      dispatch(setRefreshTimeoutId(refreshTimeoutId));
    } else {
      if(configs.env !== 'production') {
        console.log('Token already expired - performing immediate logout');
      }
      // Set session expired first
      dispatch(setSessionExpired(true));
      performLogout();
    }
  }, [dispatch, performLogout]);

  // Function to refresh the token
  const refreshToken = useCallback(async () => {
    try {
      await RefreshTokenCreateService(axios);
      
      // Hide warning and reset timers after successful refresh
      dispatch(setShowRefreshWarning(false));
      setupTokenRefresh(); // Setup new timers with refreshed token
      
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      dispatch(setShowRefreshWarning(false));
      // Set session expired first when token refresh fails
      dispatch(setSessionExpired(true));
      // Perform logout when token refresh fails
      performLogout();
      return false;
    }
  }, [dispatch, setupTokenRefresh, performLogout]);

  // Function to dismiss warning and logout
  const dismissAndLogout = useCallback(() => {
    dispatch(setShowRefreshWarning(false));
    // Set session expired first
    dispatch(setSessionExpired(true));
    // Perform logout
    performLogout();
  }, [dispatch, performLogout]);

  // Setup token refresh on mount and when token changes
  useEffect(() => {
    const apiToken = cacheGet('apiToken', true, null);
    if (apiToken?.jwtToken) {
      setupTokenRefresh();
    }

    // Add periodic check as a safety net (check every 30 seconds)
    const intervalId = setInterval(() => {
      const currentApiToken = cacheGet('apiToken', true, null);
      if (!currentApiToken || !currentApiToken.jwtToken) {
        if(configs.env !== 'production') {
          console.log('Periodic check: No token found, clearing interval');
        }
        clearInterval(intervalId);
        return;
      }

      try {
        // Try to decode JWT to get expiration time
        const tokenPayload = JSON.parse(atob(currentApiToken.jwtToken.split('.')[1]));
        if (tokenPayload.exp) {
          const expireTime = new Date(tokenPayload.exp * 1000);
          const currentTime = new Date();
          
          if (expireTime <= currentTime) {
            if(configs.env !== 'production') {
              console.log('Periodic check: Token has expired, triggering logout');
            }
            clearInterval(intervalId);
            dispatch(setSessionExpired(true));
            performLogout();
          }
        }
      } catch (error) {
        if(configs.env !== 'production') {
          console.warn('Periodic check: Could not decode JWT token:', error);
        }
      }
    }, 30000); // Check every 30 seconds

    // Cleanup timeouts and interval on unmount
    return () => {
      dispatch(clearTimeouts());
      clearInterval(intervalId);
    };
  }, [setupTokenRefresh, dispatch, performLogout]);

  return {
    showRefreshWarning,
    tokenExpireTime,
    refreshToken,
    dismissAndLogout,
    setupTokenRefresh
  };
};

export default useTokenRefresh;
